// Điều phối SCR-050 cho một domain (cơ quan hoặc địa bàn): tải cây, hiển thị bảng cây toàn màn hình,
// Drawer chi tiết (mask=false, size=480), detail+history song song qua Promise.all với AbortController,
// mở modal lệnh với Idempotency-Key tạo một lần khi mở và giữ nguyên qua các lần submit/retry,
// thành công → toast đúng tên hành động + refresh + giữ selection nếu node còn tồn tại,
// 409 → giữ form trong modal + Alert kèm "Mã hỗ trợ" + nút tải lại phiên bản.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, App as AntdApp, Button, Modal } from "antd";
import "./administration.css";
import { ApiContractError, ApiProblem, createRequestId } from "../../../api/httpClient";
import { buildTreeIndex } from "../treeIndex";
import {
  collectSelfAndDescendantIds,
  type AdminCommandOutcome,
  type AdminDetail,
  type AdminNode,
  type AdminVersion,
  type AdminWorkspaceApi,
} from "../adminTypes";
import { describeAdminError, type AdminCommandError } from "../commandErrors";
import { AdminCommandModal, type AdminCommandKind, type AdminCommandValues } from "./AdminCommandModal";
import { AdminDetailPanel } from "./AdminDetailPanel";
import { AdminTablePanel } from "./AdminTablePanel";

type TreeState =
  | Readonly<{ status: "loading" }>
  | Readonly<{ status: "ready"; nodes: AdminNode[] }>
  | Readonly<{ status: "error"; requestId?: string | undefined; code?: string | undefined }>;

type DetailState =
  | Readonly<{ status: "idle" }>
  | Readonly<{ status: "loading" }>
  | Readonly<{ status: "ready"; detail: AdminDetail; versions: AdminVersion[] }>
  | Readonly<{ status: "error"; requestId?: string | undefined; code?: string | undefined }>;

type ModalState = Readonly<{
  kind: AdminCommandKind;
  idempotencyKey: string;
  submitting: boolean;
  error: AdminCommandError | null;
}>;

export type ConflictSnapshot = Readonly<{
  absorbedVersion: number | null;
}>;

export type ReloadConflictSnapshot = (absorbedId: string | null) => Promise<ConflictSnapshot>;

function isAbortError(error: unknown): boolean {
  return typeof error === "object" && error !== null && (error as { name?: string }).name === "AbortError";
}

function extractRequestId(error: unknown): string | undefined {
  if (error instanceof ApiProblem) return error.problem.requestId;
  if (error instanceof ApiContractError) return error.requestId;
  return undefined;
}

function extractCode(error: unknown): string {
  if (error instanceof ApiProblem) return error.problem.code;
  return "UNKNOWN";
}

function successMessage(valuesKind: AdminCommandKind, outcome: AdminCommandOutcome): string {
  switch (valuesKind) {
    case "create":
      return "Tạo mới thành công";
    case "rename":
      return "Đổi tên thành công";
    case "transfer":
      return "Chuyển quản lý thành công";
    case "merge":
      return `Sáp nhập thành công — ${outcome.rehomedChildIds?.length ?? 0} đơn vị con được chuyển tiếp quản lý`;
    case "close":
      return "Đóng thành công";
    case "open":
      return "Mở lại thành công";
  }
}

export function AdminWorkspace({
  entityNoun,
  classificationLabel,
  api,
}: {
  readonly entityNoun: string;
  readonly classificationLabel: string;
  readonly api: AdminWorkspaceApi;
}) {
  const { message: messageApi } = AntdApp.useApp();
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [treeState, setTreeState] = useState<TreeState>({ status: "loading" });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ACTIVE" | "INACTIVE" | "MERGED" | undefined>(undefined);
  const [detailState, setDetailState] = useState<DetailState>({ status: "idle" });
  const [modalState, setModalState] = useState<ModalState | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setTreeState({ status: "loading" });
    api
      .getTree(controller.signal)
      .then((nodes) => {
        setTreeState({ status: "ready", nodes });
        // AC-16: Tải cây lần đầu -> selectedId = null (Drawer đóng).
        // Sau refresh, nếu node cũ còn tồn tại thì giữ; nếu đã mất -> selectedId = null.
        setSelectedId((prev) => (prev && nodes.some((node) => node.id === prev) ? prev : null));
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setTreeState({ status: "error", requestId: extractRequestId(error), code: extractCode(error) });
      });
    return () => controller.abort();
  }, [api, refreshNonce]);

  useEffect(() => {
    if (!selectedId) {
      setDetailState({ status: "idle" });
      return;
    }
    const controller = new AbortController();
    setDetailState({ status: "loading" });
    Promise.all([api.getDetail(selectedId, controller.signal), api.getHistory(selectedId, controller.signal)])
      .then(([detail, versions]) => setDetailState({ status: "ready", detail, versions }))
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setDetailState({ status: "error", requestId: extractRequestId(error), code: extractCode(error) });
      });
    return () => controller.abort();
  }, [api, selectedId, refreshNonce]);

  const nodes = useMemo(
    () => (treeState.status === "ready" ? treeState.nodes : []),
    [treeState],
  );
  const index = useMemo(() => buildTreeIndex(nodes), [nodes]);
  const byId = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);
  const detail = detailState.status === "ready" ? detailState.detail : null;
  const versions = detailState.status === "ready" ? detailState.versions : [];

  const excludedFromParent = useMemo(
    () => (selectedId ? collectSelfAndDescendantIds(index.childrenByParent, selectedId) : new Set<string>()),
    [index, selectedId],
  );

  // Hint client (backend vẫn chặn cycle/merge self): loại trừ chính node + hậu duệ và
  // các head MERGED; merge additionally loại head đã đóng.
  const transferChoices = useMemo(
    () =>
      nodes
        .filter((node) => !excludedFromParent.has(node.id) && node.status !== "MERGED")
        .map((node) => ({ id: node.id, code: node.code, name: node.name })),
    [nodes, excludedFromParent],
  );
  const mergeChoices = useMemo(
    () => transferChoices.filter((choice) => byId.get(choice.id)?.status === "ACTIVE"),
    [transferChoices, byId],
  );

  const childrenOf = useCallback(
    (id: string): AdminNode[] => index.childrenByParent.get(id) ?? [],
    [index],
  );
  const resolveVersion = useCallback(
    (id: string): Promise<number> => api.getDetail(id).then((entry) => entry.version),
    [api],
  );

  const openCommand = useCallback((kind: AdminCommandKind) => {
    setModalState({ kind, idempotencyKey: createRequestId(), submitting: false, error: null });
  }, []);

  const reloadConflictSnapshot = useCallback<ReloadConflictSnapshot>(
    async (absorbedId) => {
      if (!selectedId) throw new Error("Selected admin node is required");

      const absorbedPromise = absorbedId ? api.getDetail(absorbedId) : Promise.resolve(null);
      const [freshNodes, freshDetail, freshHistory, freshAbsorbed] = await Promise.all([
        api.getTree(),
        api.getDetail(selectedId),
        api.getHistory(selectedId),
        absorbedPromise,
      ]);

      setTreeState({ status: "ready", nodes: freshNodes });
      setDetailState({ status: "ready", detail: freshDetail, versions: freshHistory });
      return { absorbedVersion: freshAbsorbed?.version ?? null };
    },
    [api, selectedId],
  );

  const handleReloadConflictSnapshot = useCallback<ReloadConflictSnapshot>(
    async (absorbedId) => {
      const snapshot = await reloadConflictSnapshot(absorbedId);
      setModalState((prev) => (prev ? { ...prev, error: null } : prev));
      return snapshot;
    },
    [reloadConflictSnapshot],
  );

  const handleSubmit = (values: AdminCommandValues) => {
    if (!modalState) return;
    // Create không cần node đang chọn (tạo cấp gốc hoặc con trực thuộc createParentId).
    // Các lệnh còn lại thao tác trên node đang chọn → bắt buộc có detail (mang expectedVersion).
    if (values.kind !== "create" && !detail) return;
    const key = modalState.idempotencyKey;
    const expectedVersion = detail?.version ?? 0;
    setModalState((prev) => (prev ? { ...prev, submitting: true, error: null } : prev));
    const run = (): Promise<AdminCommandOutcome> | null => {
      switch (values.kind) {
        case "create":
          return api.create(
            {
              code: values.code,
              name: values.name,
              classification: values.classification,
              parentId: values.parentId,
              validFrom: values.validFrom,
              reason: values.reason,
            },
            key,
          );
        case "rename":
          return detail && api.rename(
            {
              id: detail.id,
              expectedVersion,
              newName: values.newName,
              effectiveFrom: values.effectiveFrom,
              decisionReference: values.decisionReference,
              reason: values.reason,
            },
            key,
          );
        case "transfer":
          return detail && api.transfer(
            {
              id: detail.id,
              expectedVersion,
              newParentId: values.newParentId,
              effectiveFrom: values.effectiveFrom,
              decisionReference: values.decisionReference,
              reason: values.reason,
            },
            key,
          );
        case "merge":
          return detail && api.merge(
            {
              survivorId: detail.id,
              survivorExpectedVersion: expectedVersion,
              absorbedId: values.absorbedId,
              absorbedExpectedVersion: values.absorbedExpectedVersion,
              effectiveFrom: values.effectiveFrom,
              decisionReference: values.decisionReference,
              reason: values.reason,
            },
            key,
          );
        case "close":
          return detail && api.close(
            {
              id: detail.id,
              expectedVersion,
              effectiveFrom: values.effectiveFrom,
              decisionReference: values.decisionReference,
              reason: values.reason,
            },
            key,
          );
        case "open":
          return detail && api.open(
            { id: detail.id, expectedVersion, effectiveFrom: values.effectiveFrom, reason: values.reason },
            key,
          );
      }
    };
    const command = run();
    if (!command) return;
    command
      .then((outcome) => {
        setModalState(null);
        const text = successMessage(values.kind, outcome);
        void messageApi.success(text);
        if (values.kind === "create") setSelectedId(outcome.id);
        setRefreshNonce((nonce) => nonce + 1);
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setModalState((prev) =>
          prev
            ? { ...prev, submitting: false, error: { code: extractCode(error), requestId: extractRequestId(error) } }
            : prev,
        );
      });
  };

  const treeErrorAlert =
    treeState.status === "error" ? (
      <Alert
        type="error"
        showIcon
        role="alert"
        title={describeAdminError(treeState.code ?? "UNKNOWN")}
        description={treeState.requestId ? `Mã hỗ trợ: ${treeState.requestId}` : undefined}
        action={
          <Button size="small" onClick={() => setRefreshNonce((nonce) => nonce + 1)}>
            Thử lại
          </Button>
        }
      />
    ) : null;

  const detailErrorAlert =
    detailState.status === "error" ? (
      <Alert
        type="error"
        showIcon
        role="alert"
        title={describeAdminError(detailState.code ?? "UNKNOWN")}
        description={detailState.requestId ? `Mã hỗ trợ: ${detailState.requestId}` : undefined}
        action={
          <Button size="small" onClick={() => setRefreshNonce((nonce) => nonce + 1)}>
            Thử lại
          </Button>
        }
      />
    ) : null;

  const modalTarget = detail ? { id: detail.id, code: detail.code, name: detail.name } : null;
  // Tab label + region name cho phép test/trợ năng truy cập vùng theo ngữ cảnh của từng domain.
  const regionLabel = `${entityNoun === "tổ chức" ? "Cơ quan" : "Địa bàn"}`;

  return (
    <section className="admin-workspace-region" aria-label={regionLabel}>
      <div className="admin-workspace-content">
        {treeErrorAlert ?? (
          <AdminTablePanel
            nodes={nodes}
            loading={treeState.status === "loading"}
            selectedId={selectedId}
            query={query}
            statusFilter={statusFilter}
            classificationLabel={classificationLabel}
            entityNoun={entityNoun}
            onSelect={setSelectedId}
            onChangeQuery={setQuery}
            onChangeStatus={setStatusFilter}
            onCreateRoot={() => openCommand("create")}
          />
        )}
      </div>

      <Modal
        open={selectedId !== null}
        width={900}
        centered
        destroyOnClose
        title={`Chi tiết ${entityNoun}`}
        onCancel={() => setSelectedId(null)}
        footer={null}
        className="admin-detail-modal"
        wrapClassName="admin-detail-modal-wrap"
      >
        {detailErrorAlert ?? (
          <AdminDetailPanel
            detail={detail}
            versions={versions}
            loading={detailState.status === "loading"}
            parentNodeName={detail?.parentId != null ? (byId.get(detail.parentId)?.name ?? null) : null}
            classificationLabel={classificationLabel}
            entityNoun={entityNoun}
            onAction={openCommand}
          />
        )}
      </Modal>

      {modalState && (
        <AdminCommandModal
          open
          kind={modalState.kind}
          target={modalTarget}
          createParentId={modalState.kind === "create" ? (detail?.id ?? null) : null}
          classificationLabel={classificationLabel}
          entityNoun={entityNoun}
          choices={modalState.kind === "merge" ? mergeChoices : transferChoices}
          initialValues={{ newName: detail?.name }}
          submitting={modalState.submitting}
          error={modalState.error}
          childrenOf={childrenOf}
          resolveVersion={resolveVersion}
          onReloadConflictSnapshot={handleReloadConflictSnapshot}
          onCancel={() => setModalState(null)}
          onSubmit={handleSubmit}
        />
      )}
    </section>
  );
}
